import { getUniqueColorGroupings } from '@src/quince-components/src/atoms/productCTA/utils'
import { getColorSwatchPosition } from '@src/quince-components/src/molecules/productCard/productCard.helpers'

import Product from '../domains/Product'
import { PRODUCT_TYPES } from '../domains/Product/BundleProduct'
import { ProductOptionDisplayType, ProductOptionName } from '../domains/ProductOption'
import productHelper from '../helpers/productHelper'

export const isPdpColorOrderReadyForAnalytics = ({
  isBestSellingColorsOrderingEnabled,
  isB2BMode,
  isProductRefreshLoading,
}: {
  isBestSellingColorsOrderingEnabled: boolean
  isB2BMode: boolean
  isProductRefreshLoading: boolean
}): boolean => {
  if (isB2BMode || !isBestSellingColorsOrderingEnabled) {
    return true
  }

  return !isProductRefreshLoading
}

export const getOptionValuesInOrder = (values: ({ value?: string } | string)[] | undefined): string[] =>
  values
    ?.map(value => (typeof value === 'string' ? value : value?.value))
    .filter((value): value is string => Boolean(value)) ?? []

/** Color swatch order as rendered on legacy product cards (home / CLP), not PDP `new_color_options`. */
export const getLegacyProductCardColorVariantsForAnalytics = (product: Product): string[] | undefined => {
  if (product.type === PRODUCT_TYPES.BUNDLE) {
    return undefined
  }

  const displayedColorOption = productHelper.getProductOptionToDisplayInProductCard(
    productHelper.getCascadingProductOptions(product, []),
  )
  const colorProductOption = product.productOptions?.find(option => option.name === ProductOptionName.Color)
  const displayedColorVariants = getOptionValuesInOrder(displayedColorOption?.values)
  const productColorVariants = getOptionValuesInOrder(colorProductOption?.values)
  const fallbackColorVariants = product.new_color_options?.map(item => item.value) ?? []

  if (displayedColorVariants.length > 0) {
    return displayedColorVariants
  }
  if (productColorVariants.length > 0) {
    return productColorVariants
  }
  return fallbackColorVariants.length > 0 ? fallbackColorVariants : undefined
}

interface CardVariantColorInput {
  displayConfig: { displayType: string; value: string }
}

/** Color swatch order as rendered on new product cards (cardVariants row), not catalog `colorVariants`. */
export const getNewProductCardColorVariantsForAnalytics = (
  cardVariants: CardVariantColorInput[] | undefined,
  fallbackColorVariants: string[] | undefined,
): string[] | undefined => {
  const displayedColorVariants =
    cardVariants
      ?.filter(variant => variant.displayConfig.displayType !== ProductOptionDisplayType.TEXT)
      .map(variant => variant.displayConfig.value) ?? []

  if (displayedColorVariants.length > 0) {
    return displayedColorVariants
  }

  return fallbackColorVariants && fallbackColorVariants.length > 0 ? fallbackColorVariants : undefined
}

/**
 * PDP color swatch sections in the same order as `productHelper.updateProductOptionsWithNewColorOptions`
 * (Core Color row first, then Limited Edition / other groupings).
 */
export const getPdpColorSwatchSectionsInDisplayOrder = (product: Product): string[][] | undefined => {
  if (product.type === PRODUCT_TYPES.BUNDLE) {
    return undefined
  }

  const coreColorOptionData = product.productOptions?.find(option => option.name === ProductOptionName.Color)
  const orderedColorValues = getOptionValuesInOrder(coreColorOptionData?.values)
  if (!orderedColorValues.length || !product.variants?.length) {
    return undefined
  }

  const newColorGroupings = getUniqueColorGroupings(product.variants)
  if (!newColorGroupings.length) {
    return undefined
  }

  let additionalColorOptionValues: string[] = []
  const groupedSections: string[][] = []

  newColorGroupings.forEach(colorGrouping => {
    const colorsInGroup = Array.from(
      new Set(
        product.variants
          ?.filter(variant => variant?.colorGrouping === colorGrouping.name)
          .map(variant => variant?.options?.[0]?.value)
          .filter((color): color is string => Boolean(color)),
      ),
    )

    const orderedColorsInGroup = orderedColorValues.filter(color => colorsInGroup.includes(color))
    additionalColorOptionValues = [...orderedColorsInGroup, ...additionalColorOptionValues]
    if (orderedColorsInGroup.length > 0) {
      groupedSections.push(orderedColorsInGroup)
    }
  })

  if (!groupedSections.length) {
    return undefined
  }

  const coreColorValues = orderedColorValues.filter(color => !additionalColorOptionValues.includes(color))
  return [...(coreColorValues.length > 0 ? [coreColorValues] : []), ...groupedSections]
}

/**
 * PDP color swatch order for analytics: section rows flattened in display order when grouped,
 * otherwise `productOptions` Color values (visible single-row order), then `new_color_options`.
 */
export const getPdpColorVariantsForAnalytics = (product: Product): string[] | undefined => {
  if (product.type === PRODUCT_TYPES.BUNDLE) {
    return undefined
  }

  const sections = getPdpColorSwatchSectionsInDisplayOrder(product)
  if (sections?.length) {
    const flattened = sections.flat()
    return flattened.length > 0 ? flattened : undefined
  }

  const coreColorOptionData = product.productOptions?.find(option => option.name === ProductOptionName.Color)
  const orderedColorValues = getOptionValuesInOrder(coreColorOptionData?.values)
  if (orderedColorValues.length > 0) {
    return orderedColorValues
  }

  const fallback = product.new_color_options?.map(item => item.value) ?? []
  return fallback.length > 0 ? fallback : undefined
}

/** 1-indexed swatch position within the PDP section row that contains the selected color. */
export const getPdpColorSwatchPositionForAnalytics = (
  product: Product,
  selectedColorValue: string | undefined,
  flatFallbackList: string[] | undefined,
): number | undefined => {
  const sections = getPdpColorSwatchSectionsInDisplayOrder(product)
  if (sections) {
    for (const colorsInSection of sections) {
      const position = getColorSwatchPosition(selectedColorValue, colorsInSection)
      if (position !== undefined) {
        return position
      }
    }
  }

  return getColorSwatchPosition(selectedColorValue, flatFallbackList)
}
